import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetSystemNotificationsQuery,
  useMarkSystemNotificationReadMutation,
  useClearAllSystemNotificationsMutation,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBoxOpen, faTicketAlt, faCheck, faTrash, faCircle } from '@fortawesome/free-solid-svg-icons';

const typeIcon: Record<string, any> = {
  new_ad:            faBoxOpen,
  voucher_activated: faTicketAlt,
};

const timeAgo = (dateStr: string): string => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatDate = (d: string) => {
  const dt = new Date(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dt.getDate().toString().padStart(2,'0')} ${months[dt.getMonth()]}, ${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
};

const SystemNotifications: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useGetSystemNotificationsQuery({ per_page: 20, page });
  const [markRead, { isLoading: isMarking }]   = useMarkSystemNotificationReadMutation();
  const [clearAll, { isLoading: isClearing }]  = useClearAllSystemNotificationsMutation();

  const notifications: any[] = data?.data?.data ?? [];
  const meta = data?.data;
  const unreadCount: number  = data?.unread_count ?? 0;

  const handleNotifClick = async (notif: any) => {
    if (!notif.is_read) await markRead(notif.id);
    if (notif.type === 'new_ad' && notif.reference_id) {
      navigate(`/admin/ads/${notif.reference_id}`);
    }
  };

  const handleMarkAllRead = async () => {
    await markRead('all');
    showSuccess('All notifications marked as read.');
  };

  const handleClearAll = async () => {
    const result = await Swal.fire({
      title: 'Clear all notifications?',
      text: 'This will permanently delete all notifications.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, clear all',
    });
    if (!result.isConfirmed) return;
    try {
      await clearAll(undefined).unwrap();
      showSuccess('All notifications cleared.');
      refetch();
    } catch {
      showError('Failed to clear notifications.');
    }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Notifications
                {unreadCount > 0 && (
                  <span className="badge badge-danger ms-3 fs-6 align-self-start">{unreadCount} unread</span>
                )}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary cursor-pointer" onClick={() => navigate('/admin/dashboard')}>Home</span>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Notifications</li>
              </ul>
            </div>

            <div className="d-flex align-items-center gap-3">
              {unreadCount > 0 && (
                <button
                  className="btn btn-sm btn-light-primary"
                  onClick={handleMarkAllRead}
                  disabled={isMarking}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="btn btn-sm btn-light-danger"
                  onClick={handleClearAll}
                  disabled={isClearing}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body p-0">
                {notifications.length === 0 ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-20">
                    <FontAwesomeIcon icon={faBell} style={{ fontSize: 52, opacity: 0.1 }} className="text-muted mb-4" />
                    <span className="text-muted fs-5 fw-semibold">No notifications yet</span>
                    <span className="text-muted fs-7 mt-1">You're all caught up!</span>
                  </div>
                ) : (
                  notifications.map((notif: any, idx: number) => (
                    <div
                      key={notif.id}
                      className={`d-flex align-items-start gap-4 px-7 py-5 cursor-pointer notif-row ${!notif.is_read ? 'bg-light-primary' : ''}`}
                      style={{
                        borderBottom: idx < notifications.length - 1 ? '1px solid var(--bs-border-color)' : 'none',
                        transition: 'background 0.15s',
                      }}
                      onClick={() => handleNotifClick(notif)}
                    >
                      {/* Icon */}
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{ width: 44, height: 44, background: 'rgba(57,106,84,0.1)', marginTop: 2 }}
                      >
                        <FontAwesomeIcon
                          icon={typeIcon[notif.type] ?? faBell}
                          style={{ fontSize: 18, color: '#396A54' }}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <span className="fw-bold text-gray-800 fs-6">{notif.title}</span>
                          <div className="d-flex align-items-center gap-3 flex-shrink-0">
                            {!notif.is_read && (
                              <FontAwesomeIcon icon={faCircle} style={{ fontSize: 8, color: '#396A54' }} />
                            )}
                            <span className="text-muted fs-8">{timeAgo(notif.created_at)}</span>
                          </div>
                        </div>
                        {notif.message && (
                          <div className="text-muted fs-7 mt-1">{notif.message}</div>
                        )}
                        <div className="text-muted fs-8 mt-2">{formatDate(notif.created_at)}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {meta && meta.last_page > 1 && (
                <div className="card-footer">
                  <Pagination
                    currentPage={page}
                    lastPage={meta.last_page}
                    total={meta.total}
                    onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SystemNotifications;
